import { Box, Button, Columns, Icon, Level } from 'react-bulma-components';
import React, { useEffect, useState } from 'react';
import { Sequencer, Synthetizer } from 'spessasynth_lib';
import {
  faDrum,
  faMusic,
  faPause,
  faPlay,
  faSlidersH,
  faSort,
  faStepBackward,
  faVolumeUp,
} from '@fortawesome/free-solid-svg-icons';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import IconDoubleBass from './icons/IconDoubleBass';
import IconPiano from './icons/IconPiano';
import { MIDI } from 'spessasynth_core';
import MIDIEditor from './MIDIInspector/MIDIEditor';

// ------------------------------------------------------
// Helpers
// ------------------------------------------------------
function formatTime(totalSeconds) {
  totalSeconds = Math.floor(totalSeconds);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.round(totalSeconds - minutes * 60);
  return {
    minutes,
    seconds,
    time: `${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`
  };
}

const Controller = ({ min, max, currentValue, onChange, showOutput }) => (
  <>
    <input
      id="sliderWithValue"
      style={{ margin: 0 }}
      className={`ml-1 mr-1 slider is-fullwidth ${showOutput ? 'has-output' : ''
        }`}
      step="1"
      min={min}
      max={max}
      value={currentValue}
      onChange={onChange}
      type="range"
    />
    {showOutput && (
      <output sx={{ outerWidth: 100 }} htmlFor="sliderWithValue">
        {currentValue}
      </output>
    )}
  </>
);

// ------------------------------------------------------
// Component
// ------------------------------------------------------
const Player2 = ({ midiEditor }) => {
  const [statusMsg, setStatusMsg] = useState(
    'Please wait while soundfonts are loading ...'
  );
  const [soundFontArrayBuffer, setSoundFontArrayBuffer] = useState(null);

  const [seq, setSeq] = useState(null);
  const [synth, setSynth] = useState(null);

  const [playerStatus, setPlayerStatus] = useState('paused');
  const [progress, setProgress] = useState(0);
  const [progressIntervalId, setProgressIntervalId] = useState(null);
  const [progressTime, setProgressTime] = useState('--:--/--:--');

  const [mainVolume, setMainVolume] = useState(50);
  const [mainPan, setMainPan] = useState(0);

  const [showMixer, setShowMixer] = useState(false);

  const [pianoVolume, setPianoVolume] = useState(100);
  const [bassVolume, setBassVolume] = useState(100);
  const [drumsVolume, setDrumsVolume] = useState(100);
  const [melodyVolume, setMelodyVolume] = useState(100);
  const [melodyInstrument, setMelodyInstrument] = useState(true);

  const [midiContent, setMidiContent] = useState(null);

  // ------------------------------------------------------
  // Load SoundFont
  // ------------------------------------------------------
  useEffect(() => {
    fetch('../GeneralUserGS.sf3').then(async response => {
      const buf = await response.arrayBuffer();
      setSoundFontArrayBuffer(buf);
      setStatusMsg('SoundFont has been loaded!');
    });
  }, []);

  // ------------------------------------------------------
  // Update MIDI after editing in MIDIEditor (immutabelt)
  // ------------------------------------------------------
  const updateMidiContent = midi => {
  if (!midi || !synth) return;

  // 1. Spara nuvarande position och status
  const wasPlaying = playerStatus === 'playing';
  const oldTime = seq ? seq.currentTime : 0;

  // 2. Stoppa spelaren
  if (seq) {
    seq.stop();
  }

  // 3. Skapa en NY instans så React ser en förändring
  const fresh = new MIDI(midi.writeMIDI());

  // 4. Behåll trackNames
  if (midiContent?.trackNames?.length) {
    fresh.trackNames = [...midiContent.trackNames];
  }

  setMidiContent(fresh);

  // 5. Skapa nytt outputBuffer
  const outputBuffer = fresh.writeMIDI();

  // 6. Skapa en helt ny sequencer
  const newSeq = new Sequencer([], synth, { skipToFirstNoteOn: true });
  newSeq.loop = false;
  newSeq.loadNewSongList([{ binary: outputBuffer }], false);

  // 8. Sätt ny sequencer
  setSeq(newSeq);

  // 9. Starta eller pausa beroende på tidigare status
  if (wasPlaying) {
    newSeq.currentTime = oldTime;
    //newSeq.play();
    setPlayerStatus('playing');
    StartProgress(); // din befintliga progress-funktion
  } else {
    setPlayerStatus('paused');
    setProgress((oldTime / newSeq.duration) * 1000);
    updateProgressTime(oldTime);
  }
};

  // ------------------------------------------------------
  // Load MIDI file
  // ------------------------------------------------------
  const loadMIDI = async e => {
    if (!e.target.files[0]) return;

    const midiFile = await e.target.files[0].arrayBuffer();
    const parsedMIDI = new MIDI(midiFile);

    // Normalisera så första noten börjar vid 0
    const firstNoteTicks = parsedMIDI.firstNoteOn;
    parsedMIDI.tracks.forEach(track =>
      track.forEach(ev => {
        ev.ticks = Math.max(0, ev.ticks - firstNoteTicks);
      })
    );
    parsedMIDI.flush();

    setMidiContent(parsedMIDI);

    const outputBuffer = parsedMIDI.writeMIDI();

    const context = new AudioContext();
    await context.audioWorklet.addModule('../worklet_processor.min.js');

    const newSynth = new Synthetizer(context.destination, soundFontArrayBuffer);
    setSynth(newSynth);

    const sequencer = new Sequencer([], newSynth, {
      skipToFirstNoteOn: true
    });
    sequencer.loop = false;
    sequencer.loadNewSongList([{ binary: outputBuffer }], false);

    setSeq(sequencer);
    setPlayerStatus('paused');
    setProgress(0);
    setProgressTime('--:--/--:--');
  };

  // ------------------------------------------------------
  // Progress handling
  // ------------------------------------------------------
  const updateProgressTime = currentTime => {
    if (!seq) {
      setProgressTime('--:--/--:--');
      return;
    }
    const time = formatTime(currentTime);
    const total = formatTime(seq.duration);
    setProgressTime(`${time.time}/${total.time}`);
  };

  const StartProgress = () => {
    if (progressIntervalId !== null || !seq) return;

    const id = setInterval(() => {
      if (!seq) return;

      const ratio = seq.duration > 0 ? seq.currentTime / seq.duration : 0;
      setProgress(ratio * 1000);
      updateProgressTime(seq.currentTime);

      if (ratio >= 1) {
        clearInterval(id);
        setProgressIntervalId(null);
        setPlayerStatus('paused');
        setProgress(0);
        updateProgressTime(0);
      }
    }, 100);

    setProgressIntervalId(id);
  };

  const StopProgress = () => {
    if (progressIntervalId !== null) {
      clearInterval(progressIntervalId);
      setProgressIntervalId(null);
    }
  };

  // ------------------------------------------------------
  // Transport controls
  // ------------------------------------------------------
  const start = () => {
    if (!seq) return;
    seq.play(true);
    StartProgress();
    setPlayerStatus('playing');
  };

  const play = () => {
    if (!seq) return;

    const atEnd = (progress * seq.duration) / 1000 >= seq.duration;
    if (atEnd) {
      setProgress(0);
      updateProgressTime(0);
      return;
    }

    seq.play();
    StartProgress();
    setPlayerStatus('playing');
  };

  const pause = () => {
    if (!seq) return;

    seq.pause();
    StopProgress();
    setPlayerStatus('paused');
  };

  const setPlaytime = val => {
    if (!seq) return;

    const numVal = Number(val);
    const time = (numVal * seq.duration) / 1000;

    if (playerStatus === 'playing') {
      seq.currentTime = time;
    } else {
      setProgress(numVal);
      updateProgressTime(time);
    }
  };

  // ------------------------------------------------------
  // Mixer controls
  // ------------------------------------------------------
  const adjustMainVolume = val => {
    const v = Math.round(val);
    setMainVolume(v);
    synth?.setMainVolume(v / 100);
  };

  const adjustMainPan = val => {
    setMainPan(val);
    synth?.setMasterPan(val / 100);
  };

  const adjustChannelVolume = (channel, val, setter) => {
    const v = Math.round(val);
    setter(v);
    if (!synth) return;

    synth.lockController(channel, 7, false);
    synth.controllerChange(channel, 7, v);
    synth.lockController(channel, 7, true);
  };

  // Save for later use
/*   const toggleInstrument = () => {
    if (synth) {
      synth.programChange(2, melodyInstrument ? 72 : 0);
    }
    setMelodyInstrument(!melodyInstrument);
  };

  const setFlute = () => {
    if (!synth) return;
    synth.programChange(2, 72);
    synth.lockController(2, -1, true);
  }; */

  // ------------------------------------------------------
  // Render
  // ------------------------------------------------------
  return (
    <Columns centered>
      <Columns.Column narrow>
        <audio controls src="">
          Your browser does not support the HTML5 Audio element.
        </audio>

        <div>[Player] {statusMsg}</div>
        <div>
          <input
            onChange={loadMIDI}
            accept=".mid, .rmi"
            id="midi_input"
            type="file"
          />
        </div>

        <Box>
          <Level>
            <Level.Side align="left">
              <Level.Item>
                <Button.Group hasAddons size="small" marginless>
                  <Button
                    onClick={start}
                    disabled={progress < 1 || !seq}
                    renderAs="span"
                  >
                    <Icon>
                      <FontAwesomeIcon icon={faStepBackward} />
                    </Icon>
                  </Button>

                  {playerStatus !== 'playing' ? (
                    <Button onClick={play} renderAs="span" disabled={!seq}>
                      <Icon>
                        <FontAwesomeIcon icon={faPlay} />
                      </Icon>
                    </Button>
                  ) : (
                    <Button onClick={pause} renderAs="span">
                      <Icon>
                        <FontAwesomeIcon icon={faPause} />
                      </Icon>
                    </Button>
                  )}
                </Button.Group>
              </Level.Item>

              <Level.Item>
                <div className="is-family-monospace is-size-7">
                  {progressTime}
                </div>
              </Level.Item>
            </Level.Side>

            <Level.Item>
              <Controller
                min={0}
                max={1000}
                currentValue={progress}
                onChange={e => setPlaytime(e.target.value)}
              />
            </Level.Item>

            <Level.Side align="right">
              <Level.Item>
                <Button
                  size="small"
                  onClick={() => setShowMixer(!showMixer)}
                  renderAs="span"
                >
                  <Icon>
                    <FontAwesomeIcon icon={faSlidersH} />
                  </Icon>
                </Button>
              </Level.Item>
            </Level.Side>
          </Level>

          {/* Mixer */}
          {showMixer && (
            <>
              <Level style={{ marginBottom: 16 }}>
                <Level.Item>
                  <Icon>
                    <FontAwesomeIcon icon={faVolumeUp} />
                  </Icon>
                  <Controller
                    min={0}
                    max={100}
                    currentValue={mainVolume}
                    onChange={e => adjustMainVolume(+e.target.value)}
                    showOutput={false}
                  />
                </Level.Item>
              </Level>

              <Level>
                <Level.Item>
                  <Icon>
                    <IconPiano />
                  </Icon>
                  <Controller
                    min={0}
                    max={100}
                    currentValue={pianoVolume}
                    onChange={e =>
                      adjustChannelVolume(0, +e.target.value, setPianoVolume)
                    }
                    showOutput={false}
                  />
                </Level.Item>
              </Level>

              <Level>
                <Level.Item>
                  <Icon>
                    <IconDoubleBass />
                  </Icon>
                  <Controller
                    min={0}
                    max={100}
                    currentValue={bassVolume}
                    onChange={e =>
                      adjustChannelVolume(1, +e.target.value, setBassVolume)
                    }
                    showOutput={false}
                  />
                </Level.Item>
              </Level>

              <Level>
                <Level.Item>
                  <Icon>
                    <FontAwesomeIcon icon={faDrum} />
                  </Icon>
                  <Controller
                    min={0}
                    max={100}
                    currentValue={drumsVolume}
                    onChange={e =>
                      adjustChannelVolume(9, +e.target.value, setDrumsVolume)
                    }
                    showOutput={false}
                  />
                </Level.Item>
              </Level>

              <Level>
                <Level.Item>
                  <Icon>
                    <FontAwesomeIcon icon={faMusic} />
                  </Icon>
                  <Controller
                    min={0}
                    max={100}
                    currentValue={melodyVolume}
                    onChange={e =>
                      adjustChannelVolume(2, +e.target.value, setMelodyVolume)
                    }
                    showOutput={false}
                  />
                </Level.Item>
              </Level>
            </>
          )}
        </Box>

        {/* Valfritt: panning UI om du vill använda mainPan */}
        {false && (
          <Columns className="is-vcentered">
            <Columns.Column size="1">
              <Icon align="left" pr={2}>
                <FontAwesomeIcon icon={faVolumeUp} />
              </Icon>
            </Columns.Column>
            <Columns.Column size="11">
              <Controller
                min={0}
                max={100}
                currentValue={mainVolume}
                onChange={e => adjustMainVolume(+e.target.value)}
                showOutput={false}
              />
            </Columns.Column>
            <Columns.Column size="1">
              <Icon align="left" mt={1} pr={3}>
                <FontAwesomeIcon icon={faSort} className="fa-rotate-90" />
              </Icon>
            </Columns.Column>
            <Columns.Column size="11">
              <Controller
                min={-100}
                max={100}
                currentValue={mainPan}
                onChange={e => adjustMainPan(+e.target.value)}
                showOutput={false}
              />
            </Columns.Column>
          </Columns>
        )}

        <div>
          Status: {playerStatus} – {seq?.paused ? 'Paused' : 'Playing|Stopped'}
        </div>
        <div>Channels: {synth?.channelProperties.length}</div>
        {midiEditor === true && 
        <MIDIEditor
          parsedMIDI={midiContent}
          onParsedMIDIChange={updateMidiContent}
        />}
      </Columns.Column>
    </Columns>
  );
};

export default Player2;